import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import {
  clearRentalsForExcel,
  getRentalsForExcel,
} from "../../actions/rentals";
import { strings } from "../../I18n";
import { buildWhitelabelFilter } from "../../utils/filters";
import { reformatFilter, reformatSearch } from "../../utils/utils";
import { toRentalExportRows } from "../../v2/utils/rental-export-rows";
import ModalContainer from "../modal/modal";

enum ExportMode {
  CURRENT = "current",
  ALL = "all",
}

interface Props {
  onSuccess: () => void;
  onError: () => void;
  onClose: () => void;
  rentals: any[];
  rentalsResult: any;
  searchFilter: any;
  searchValue: string;
  orderValue: string;
}

export const RentalsExportBox = ({
  onSuccess,
  onError,
  onClose,
  rentals,
  rentalsResult,
  searchFilter,
  searchValue,
  orderValue,
}: Props) => {
  const dispatch = useDispatch();
  const [exportMode, setExportMode] = useState<ExportMode>(ExportMode.CURRENT);
  const [isLoading, setIsLoading] = useState(false);

  const savedCity = useSelector((state: any) => state.cities.savedCity);
  const savedWhitelabel = useSelector(
    (state: any) => state.cities.savedWhitelabel
  );
  const filterValue = useSelector((state: any) => state.screen.filterValue);
  const rentalsForExcel = useSelector(
    (state: any) => state.rentals.rentalsForExcel
  );
  const getRentalsForExcelSuccess = useSelector(
    (state: any) => state.rentals.getRentalsForExcelSuccess
  );

  const headers = [
    { label: strings("descriptions.rentalId"), key: "id" },
    { label: strings("descriptions.userName"), key: "userName" },
    { label: strings("descriptions.bikeName"), key: "vehicleName" },
    { label: strings("descriptions.transactionLink"), key: "paymentId" },
    { label: strings("descriptions.startStation"), key: "startStationName" },
    { label: strings("descriptions.startDock"), key: "startStationCode" },
    { label: strings("descriptions.startDate"), key: "startTime" },
    { label: strings("descriptions.endStation"), key: "endStationName" },
    { label: strings("descriptions.endDock"), key: "endDockCode" },
    { label: strings("descriptions.endDate"), key: "endTime" },
    { label: strings("descriptions.duration"), key: "duration" },
    { label: strings("descriptions.cityProject"), key: "cityId" },
    { label: strings("descriptions.vehicleCode"), key: "vehicleCode" },
    { label: strings("descriptions.lockCode"), key: "lockCode" },
    { label: strings("descriptions.status"), key: "status" },
  ];

  const exportData = useMemo(() => {
    if (exportMode === ExportMode.CURRENT) {
      return toRentalExportRows(rentals || []);
    }
    return toRentalExportRows(rentalsForExcel || []);
  }, [exportMode, rentals, rentalsForExcel]);

  const isDataReady = useMemo(() => {
    if (exportMode === ExportMode.CURRENT) {
      return rentals?.length > 0;
    }
    return getRentalsForExcelSuccess === true && rentalsForExcel?.length > 0;
  }, [exportMode, rentals, getRentalsForExcelSuccess, rentalsForExcel]);

  const totalElements = rentalsResult?.totalElements || 0;

  useEffect(() => {
    if (getRentalsForExcelSuccess === true) {
      setIsLoading(false);
    } else if (getRentalsForExcelSuccess === false) {
      setIsLoading(false);
      onError();
    }
  }, [getRentalsForExcelSuccess, onError]);

  useEffect(() => {
    return () => {
      dispatch(clearRentalsForExcel());
    };
  }, [dispatch]);

  const handleExportModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as ExportMode;
    setExportMode(value);

    if (value === ExportMode.ALL && !rentalsForExcel?.length) {
      setIsLoading(true);
      const whitelabelFilter = buildWhitelabelFilter(
        savedCity,
        savedWhitelabel
      );
      dispatch(
        getRentalsForExcel(
          0,
          reformatSearch(searchFilter, searchValue),
          orderValue,
          reformatFilter(filterValue) +
            (savedCity ? "&" + savedCity : "") +
            whitelabelFilter,
          totalElements
        ) as any
      );
    }
  };

  const handleDownloadClick = () => {
    setTimeout(() => {
      onSuccess();
    }, 100);
  };

  return (
    <ModalContainer visible={true} closeAction={onClose}>
      <div>
        <Typography variant="h6" gutterBottom={true}>
          Export Rentals to CSV
        </Typography>

        <FormControl>
          <RadioGroup
            defaultValue={ExportMode.CURRENT}
            name="radio-buttons-group"
            onChange={handleExportModeChange}
          >
            <FormControlLabel
              value={ExportMode.CURRENT}
              control={<Radio />}
              label={`Export Current Page (${rentals?.length || 0} records)`}
            />
            <FormControlLabel
              value={ExportMode.ALL}
              control={<Radio />}
              label={`Export All (${totalElements} records, with applied filters)`}
            />
          </RadioGroup>
        </FormControl>

        {isLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading {totalElements} records...
            </Typography>
          </Box>
        )}

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}
        >
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          {isDataReady && !isLoading ? (
            <CSVLink
              data={exportData}
              headers={headers}
              filename="rentals.csv"
              onClick={handleDownloadClick}
              style={{ textDecoration: "none" }}
            >
              <Button variant="contained">Export</Button>
            </CSVLink>
          ) : (
            <Button variant="contained" disabled={true}>
              Export
            </Button>
          )}
        </Box>
      </div>
    </ModalContainer>
  );
};
